import { AddressAutocompleteInput } from '../../components/common/AddressAutocompleteInput';
import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
// frontend/src/pages/maintenance/ContractsList.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Input,
  Select,
  Option,
  Stack,
  CircularProgress,
  Alert,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  ModalClose,
  FormControl,
  FormLabel,
  Autocomplete,
  Switch,
} from '@mui/joy';
const PAGE_SIZE = 20;
import {
  Plus,
  MagnifyingGlass as Search,
  Funnel as Filter,
  FileText,
  X,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractApi, contractTemplateApi } from '../../lib/maintenanceApi';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import type {
  ContractStatus,
  ContractFilters,
  MaintenanceContractTemplate,
  MaintenanceFrequency,
} from '../../types/maintenance';
import {
  CONTRACT_STATUS_LABELS,
  FREQUENCY_LABELS,
} from '../../types/maintenance';
import { apiClient } from '../../lib/api';
import type { Contact } from '../../types/crm';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { MaintenanceCoverage } from './ContractWorkflows';
import { useDebounce } from '../../hooks/useDebounce';
import { applyPrefill, contactAddressFields } from '../../utils/prefill';

interface ChecklistOption {
  id: number;
  title: string;
  description?: string;
  steps_count?: number;
}

interface ContractsListProps {
  embedded?: boolean;
}

const ContractsList: React.FC<ContractsListProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [renewal, setRenewal] = useState('');
  const search = useDebounce(searchQuery, 250);
  const [statusFilter, setStatusFilter] = useState<ContractStatus | ''>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, renewal]);

  // Build filters
  const filters: ContractFilters = { page: currentPage, search, renewal };
  if (statusFilter) filters.status = statusFilter;

  // Fetch contracts
  const { data: contractsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['maintenance-contracts', filters],
    queryFn: () => contractApi.list(filters),
  });

  const contracts = contractsResponse?.results || [];
  const totalCount = contractsResponse?.count ?? 0;

  const filteredContracts = contracts;

  return (
    <Box sx={embedded ? {} : { p: { xs: 1.5, md: 3 } }}>
      {/* Header - only show when not embedded */}
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography level="h2">Maintenance Contracts</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Manage customer maintenance agreements
            </Typography>
          </Box>
          <Button
            startDecorator={<Plus size={18} />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            New Contract
          </Button>
        </Box>
      )}

      {/* Action bar when embedded */}
      {embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            startDecorator={<Plus size={18} />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            New Contract
          </Button>
        </Box>
      )}

      <MaintenanceCoverage />
      {error && <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Could not load contracts.</Alert>}
      {/* Filters */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2, flexWrap: 'wrap' }}>
            <Select aria-label="Renewal queue" value={renewal} onChange={(_, v) => setRenewal(v || '')}><Option value="">All contracts</Option><Option value="expiring">Expiring in 30 days</Option><Option value="expired">Expired</Option><Option value="decision">Renewal decision pending</Option></Select>
            <Input
              placeholder="Search contracts..."
              startDecorator={<Search size={18} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 250 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value as ContractStatus | '')}
              startDecorator={<Filter size={18} />}
              sx={{ minWidth: 150 }}
            >
              <Option value="">All Statuses</Option>
              {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
            {(searchQuery || statusFilter) && (
              <Button
                variant="plain"
                color="neutral"
                startDecorator={<X size={18} />}
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Contracts Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredContracts.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <FileText size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <Typography level="h4" sx={{ mb: 1 }}>
              No contracts found
            </Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
              {searchQuery || statusFilter
                ? 'Try adjusting your filters'
                : 'Create your first maintenance contract to get started'}
            </Typography>
            {!searchQuery && !statusFilter && (
              <Button
                startDecorator={<Plus size={18} />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                Create Contract
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <ResponsiveTable rows={filteredContracts} getRowKey={row => row.id} columns={[
          { key: 'contract', header: 'Contract', mobileVariant: 'header', render: row => <Button variant="plain" onClick={() => navigate(`/maintenance/contracts/${row.id}`)}>{row.contract_number}</Button> },
          { key: 'customer', header: 'Customer', render: row => row.contact_name },
          { key: 'address', header: 'Property', render: row => row.full_property_address },
          { key: 'status', header: 'Status', render: row => <Chip size="sm">{CONTRACT_STATUS_LABELS[row.status]}</Chip> },
          { key: 'terms', header: 'Service terms', render: row => `${FREQUENCY_LABELS[row.effective_frequency]} · $${row.effective_fee} / visit` },
          { key: 'renewal', header: 'Renewal owner / end', render: row => `${row.renewal_owner_name || 'Unassigned'} · ${row.end_date || 'No end date'}` },
          { key: 'action', header: 'Next action', render: row => `${row.next_action || 'No action set'}${row.next_action_date ? ` · ${row.next_action_date}` : ''}` },
        ]} />
      )}

      {/* Pagination */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography level="body-sm" color="neutral">
          {totalCount > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} contracts`
            : 'No contracts'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Create Contract Modal */}
      <CreateContractModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={(id) => {
          queryClient.invalidateQueries({ queryKey: ['maintenance-contracts'] });
          setIsCreateModalOpen(false);
          navigate(`/maintenance/contracts/${id}`);
        }}
      />
    </Box>
  );
};

// Create Contract Modal Component
interface CreateContractModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}

const CreateContractModal: React.FC<CreateContractModalProps> = ({ open, onClose, onCreated }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<MaintenanceContractTemplate | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [propertyAddress, setPropertyAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [frequency, setFrequency] = useState<MaintenanceFrequency>('annual');
  const [feePerAppointment, setFeePerAppointment] = useState('');
  const [nte, setNte] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch contacts for autocomplete
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await apiClient.get('/api/crm/contacts/');
      return response.data as { results: Contact[] };
    },
  });

  const contacts = contactsData?.results || [];

  // Fetch the org's maintenance plans
  const { data: templatesData } = useQuery({
    queryKey: ['maintenance-contract-templates'],
    queryFn: () => contractTemplateApi.list(),
  });
  const templates = (templatesData?.results || []).filter((template) => template.is_active);

  // Fetch checklists
  const { data: checklistsData } = useQuery({
    queryKey: ['checklists'],
    queryFn: () => ChecklistsAPI.list(),
  });
  const checklists = checklistsData?.results || [];

  // Picking a plan is an explicit request for its terms, so its cadence and
  // renewal setting are applied; the fee only fills a blank so a price the
  // user already typed is never overwritten. All three stay editable.
  //
  // The one price we will replace is the one we put there ourselves: switching
  // from one plan to another must reprice, or the contract is filed against
  // plan B at plan A's money.
  const seededFeeRef = useRef<string | null>(null);
  const applyTemplate = (template: MaintenanceContractTemplate | null) => {
    setSelectedTemplate(template);
    if (!template) return;

    setFrequency(template.frequency);
    setAutoRenew(template.auto_renew);
    if (!feePerAppointment || feePerAppointment === seededFeeRef.current) {
      setFeePerAppointment(template.fee_per_appointment);
      seededFeeRef.current = template.fee_per_appointment;
    }
  };

  // Terms still matching the plan are left unset rather than copied, so a
  // later edit to the plan flows through to this contract instead of being
  // frozen at whatever was on screen the day it was created.
  const inheritsFrequency = !!selectedTemplate && frequency === selectedTemplate.frequency;
  const inheritsFee = !!selectedTemplate && feePerAppointment === selectedTemplate.fee_per_appointment;

  const createMutation = useMutation({
    mutationFn: () =>
      contractApi.create({
        contact_id: selectedContact!.id,
        template_id: selectedTemplate?.id ?? null,
        property_address_line1: propertyAddress.line1,
        property_address_line2: propertyAddress.line2,
        property_city: propertyAddress.city,
        property_state: propertyAddress.state,
        property_zip: propertyAddress.zip,
        start_date: startDate,
        end_date: endDate || undefined,
        ...(inheritsFrequency ? {} : { custom_frequency: frequency }),
        ...(inheritsFee ? {} : { custom_fee: feePerAppointment }),
        nte: nte || undefined,
        auto_renew: autoRenew,
        checklist_id: selectedChecklist?.id || undefined,
        status: 'draft',
      }),
    onSuccess: (contract) => {
      onCreated(contract.id);
    },
  });

  const handleSubmit = async () => {
    if (!selectedContact || !propertyAddress.line1 || !startDate || !frequency || !feePerAppointment) return;
    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Seed the property address from the selected customer.
  //
  // This used to be a "Copy address from contact" button gated on
  // `selectedContact.addresses?.length`, but the contacts list endpoint never
  // returned an `addresses` array - so the guard was always false, the button
  // never rendered, and the handler was unreachable. The list now carries
  // `primary_address_detail`, and copying on select is one fewer click than
  // the button ever was. Only empty fields are filled, so a property address
  // the user has already typed is never overwritten.
  useEffect(() => {
    const address = contactAddressFields(selectedContact);
    if (!address) return;

    setPropertyAddress(prev =>
      applyPrefill(prev, {
        line1: address.address_1,
        line2: address.address_2,
        city: address.city,
        state: address.state,
        zip: address.zip_code,
      }),
    );
  }, [selectedContact]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 500, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', overflow: 'hidden' }}>
        <ModalClose />
        <DialogTitle sx={{ pr: 3, flexShrink: 0 }}>Create Maintenance Contract</DialogTitle>

        <DialogContent sx={{ minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          <Stack spacing={2} sx={{ py: 1 }}>
            {templates.length > 0 && (
              <FormControl>
                <FormLabel>Maintenance Plan</FormLabel>
                <Autocomplete
                  placeholder="Start from a plan..."
                  options={templates}
                  getOptionLabel={(option) =>
                    `${option.name} — ${FREQUENCY_LABELS[option.frequency]}, $${option.fee_per_appointment}`
                  }
                  value={selectedTemplate}
                  onChange={(_, value) => applyTemplate(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                  Terms you leave unchanged stay linked to the plan, so later plan updates carry over
                </Typography>
              </FormControl>
            )}

            <FormControl required>
              <FormLabel>Customer</FormLabel>
              <Autocomplete
                placeholder="Search contacts..."
                options={contacts}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                value={selectedContact}
                onChange={(_, value) => setSelectedContact(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Property Address</FormLabel>
              <AddressAutocompleteInput
                value={propertyAddress.line1}
                onChange={(value) => setPropertyAddress(previous => ({ ...previous, line1: value }))}
                onAddressSelect={(parsed) => setPropertyAddress(previous => ({ ...previous, line1: parsed.address_1, city: parsed.city, state: parsed.state, zip: parsed.zip_code }))}
              />
            </FormControl>

            <Input
              placeholder="Apt, suite, etc. (optional)"
              value={propertyAddress.line2}
              onChange={(e) => setPropertyAddress({ ...propertyAddress, line2: e.target.value })}
            />

            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              <FormControl sx={{ flex: 2, minWidth: 0 }} required>
                <FormLabel>City</FormLabel>
                <Input
                  value={propertyAddress.city}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, city: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }} required>
                <FormLabel>State</FormLabel>
                <Input
                  value={propertyAddress.state}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, state: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }} required>
                <FormLabel>ZIP</FormLabel>
                <Input
                  value={propertyAddress.zip}
                  onChange={(e) => setPropertyAddress({ ...propertyAddress, zip: e.target.value })}
                />
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>Frequency</FormLabel>
                <Select
                  value={frequency}
                  onChange={(_, value) => setFrequency(value as MaintenanceFrequency)}
                >
                  {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                <FormLabel>Fee Per Appointment</FormLabel>
                <Input
                  type="number"
                  startDecorator="$"
                  placeholder="0.00"
                  value={feePerAppointment}
                  onChange={(e) => setFeePerAppointment(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>NTE (Not To Exceed)</FormLabel>
              <Input
                type="number"
                startDecorator="$"
                placeholder="0.00"
                value={nte}
                onChange={(e) => setNte(e.target.value)}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                Maximum repair amount before requiring client approval
              </Typography>
            </FormControl>

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <FormLabel>Auto-Renew Contract</FormLabel>
              <Switch
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Linked Checklist (optional)</FormLabel>
              <Autocomplete
                placeholder="Select a checklist..."
                options={checklists}
                getOptionLabel={(option) => option.title}
                value={selectedChecklist}
                onChange={(_, value) => setSelectedChecklist(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                Technicians will complete this checklist during appointments
              </Typography>
            </FormControl>

          </Stack>
        </DialogContent>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexShrink: 0, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!selectedContact || !propertyAddress.line1 || !startDate || !frequency || !feePerAppointment}
          >
            Create Contract
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default ContractsList;
